import { supabase } from './supabaseClient';

export interface Fournisseur {
  id: string;
  code: string;
  name: string;
  address: string | null;
  email: string | null;
  phone: string | null;
  vat_number: string | null;
  comment: string | null;
  active: boolean;
  created_at: string;
}

export interface ArticleFournisseur {
  id: string;
  article_id: string;
  fournisseur_id: string;
  code_article_fournisseur: string | null;
  prix_achat: number | null;
  is_default: boolean;
  created_at: string;
  fournisseurs?: Fournisseur | null;
}

export interface CreateFournisseurData {
  code: string;
  name: string;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  vat_number?: string | null;
  comment?: string | null;
  active?: boolean;
}

export interface UpdateFournisseurData {
  code?: string;
  name?: string;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  vat_number?: string | null;
  comment?: string | null;
  active?: boolean;
}

export interface LinkArticleFournisseurData {
  article_id: string;
  fournisseur_id: string;
  code_article_fournisseur?: string | null;
  prix_achat?: number | null;
  is_default?: boolean;
}

export interface FournisseursPaginatedResponse {
  fournisseurs: Fournisseur[];
  totalCount: number;
}

export const fournisseurService = {
  async getFournisseurs(activeOnly = true): Promise<Fournisseur[]> {
    let query = supabase.from('fournisseurs').select('*').order('name', { ascending: true });
    if (activeOnly) {
      query = query.eq('active', true);
    }
    const { data, error } = await query;
    if (error) {
      console.error('Erreur getFournisseurs:', error);
      return [];
    }
    return (data || []) as Fournisseur[];
  },

  async getFournisseursPaginated(
    page: number = 1,
    limit: number = 10,
    searchTerm: string = ''
  ): Promise<FournisseursPaginatedResponse> {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from('fournisseurs')
        .select('*', { count: 'exact' })
        .eq('active', true)
        .order('name', { ascending: true })
        .range(from, to);

      if (searchTerm.trim()) {
        query = query.or(`code.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Erreur getFournisseursPaginated:', error);
        return { fournisseurs: [], totalCount: 0 };
      }

      return {
        fournisseurs: (data || []) as Fournisseur[],
        totalCount: count ?? 0,
      };
    } catch (error) {
      console.error('Erreur getFournisseursPaginated:', error);
      return { fournisseurs: [], totalCount: 0 };
    }
  },

  async deleteFournisseur(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.from('fournisseurs').update({ active: false }).eq('id', id);
      if (error) {
        console.error('Erreur deleteFournisseur:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la suppression.';
      return { success: false, error: message };
    }
  },

  async getFournisseurById(id: string): Promise<Fournisseur | null> {
    const { data, error } = await supabase.from('fournisseurs').select('*').eq('id', id).single();
    if (error) {
      console.error('Erreur getFournisseurById:', error);
      return null;
    }
    return data as Fournisseur;
  },

  async createFournisseur(payload: CreateFournisseurData): Promise<Fournisseur | null> {
    const { data, error } = await supabase
      .from('fournisseurs')
      .insert({
        code: payload.code,
        name: payload.name,
        address: payload.address ?? null,
        email: payload.email ?? null,
        phone: payload.phone ?? null,
        vat_number: payload.vat_number ?? null,
        comment: payload.comment ?? null,
        active: payload.active ?? true,
      })
      .select()
      .single();
    if (error) {
      console.error('Erreur createFournisseur:', error);
      return null;
    }
    return data as Fournisseur;
  },

  async updateFournisseur(id: string, payload: UpdateFournisseurData): Promise<Fournisseur | null> {
    const { data, error } = await supabase
      .from('fournisseurs')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Erreur updateFournisseur:', error);
      return null;
    }
    return data as Fournisseur;
  },

  async getArticleFournisseurs(articleId: string): Promise<ArticleFournisseur[]> {
    const { data, error } = await supabase
      .from('article_fournisseurs')
      .select('*, fournisseurs(*)')
      .eq('article_id', articleId)
      .order('is_default', { ascending: false });
    if (error) {
      console.error('Erreur getArticleFournisseurs:', error);
      return [];
    }
    return (data || []) as ArticleFournisseur[];
  },

  async linkArticleFournisseur(
    payload: LinkArticleFournisseurData
  ): Promise<ArticleFournisseur | null> {
    const { data, error } = await supabase
      .from('article_fournisseurs')
      .insert({
        article_id: payload.article_id,
        fournisseur_id: payload.fournisseur_id,
        code_article_fournisseur: payload.code_article_fournisseur ?? null,
        prix_achat: payload.prix_achat ?? null,
        is_default: payload.is_default ?? false,
      })
      .select()
      .single();
    if (error) {
      console.error('Erreur linkArticleFournisseur:', error);
      return null;
    }
    return data as ArticleFournisseur;
  },

  async unlinkArticleFournisseur(articleId: string, fournisseurId: string): Promise<boolean> {
    const { error } = await supabase
      .from('article_fournisseurs')
      .delete()
      .eq('article_id', articleId)
      .eq('fournisseur_id', fournisseurId);
    if (error) {
      console.error('Erreur unlinkArticleFournisseur:', error);
      return false;
    }
    return true;
  },

  async setArticleFournisseurDefault(articleId: string, fournisseurId: string): Promise<boolean> {
    const { error: resetError } = await supabase
      .from('article_fournisseurs')
      .update({ is_default: false })
      .eq('article_id', articleId);
    if (resetError) {
      console.error('Erreur reset is_default:', resetError);
      return false;
    }
    const { error } = await supabase
      .from('article_fournisseurs')
      .update({ is_default: true })
      .eq('article_id', articleId)
      .eq('fournisseur_id', fournisseurId);
    if (error) {
      console.error('Erreur setArticleFournisseurDefault:', error);
      return false;
    }
    return true;
  },
};
